import { QueryClientProvider } from '@tanstack/react-query';
import { Stack } from 'expo-router';
import { Component, useEffect, type ReactNode } from 'react';
import { Platform, ScrollView, Text, View, StyleSheet } from 'react-native';
import * as SplashScreen from 'expo-splash-screen';

import { queryClient } from '@/lib/queryClient';
import { applyPreferredLocale } from '@/lib/i18n';
import { ThemeProvider, useAppFonts, palette } from '@/theme';

class ErrorCatcher extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // surface to console so logs capture it
    console.log('[ROOT ERROR]', error?.message, '\n', error?.stack, '\n', info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <ScrollView style={{ flex: 1, padding: 24, backgroundColor: palette.paper }}>
          <Text style={{ fontFamily: 'monospace', fontSize: 14, color: '#a00' }}>
            {String(this.state.error?.message)}
            {'\n\n'}
            {String(this.state.error?.stack || '')}
          </Text>
        </ScrollView>
      );
    }
    return this.props.children;
  }
}

SplashScreen.preventAutoHideAsync().catch(() => undefined);

export default function RootLayout() {
  const [fontsLoaded] = useAppFonts();

  useEffect(() => {
    void applyPreferredLocale().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (fontsLoaded) {
      SplashScreen.hideAsync().catch(() => undefined);
    }
  }, [fontsLoaded]);

  // Only native gates on fonts (no font-display there). On web the static HTML
  // must contain the real page, and fonts swap in via font-display: swap.
  if (Platform.OS !== 'web' && !fontsLoaded) {
    return <View style={[styles.gate, { backgroundColor: palette.paper }]} />;
  }

  return (
    <ErrorCatcher>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider mood="aurora">
          <Stack screenOptions={{ headerShown: false, contentStyle: { backgroundColor: palette.paper } }} />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorCatcher>
  );
}

const styles = StyleSheet.create({
  gate: { flex: 1 },
});
